import { useRef, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { mergeApi, type MergeFailure } from '../features/merge/api.ts';
import { MergePreview } from '../features/merge/MergePreview.tsx';

const MAX_FILES = 50;
const MAX_BYTES = 25 * 1024 * 1024;

interface Item {
  id: string;
  file: File;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isPdf(f: File): boolean {
  return f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
}

const IMAGE_EXT = /\.(jpe?g|png|webp|heic|heif|gif|tiff?|bmp|jp2|avif)$/i;
function isSupported(f: File): boolean {
  return isPdf(f) || f.type.startsWith('image/') || IMAGE_EXT.test(f.name);
}

const ERROR_TEXT: Record<string, string> = {
  need_at_least_two_files: 'Add at least two files to merge.',
  unsupported_or_corrupt_image: 'One image couldn’t be read',
  corrupt_or_encrypted_pdf: 'One PDF is corrupt or password-protected',
  merge_failed: 'Merge failed. Please try again.',
};

export function MergePdf(): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [filename, setFilename] = useState('');
  const [pending, setPending] = useState<false | 'merge' | 'compress'>(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragIndex = useRef<number | null>(null);

  function addFiles(list: FileList | null | undefined): void {
    setError(null);
    if (!list || list.length === 0) return;
    const incoming: Item[] = [];
    const rejected: string[] = [];
    for (const f of Array.from(list)) {
      if (!isSupported(f)) { rejected.push(`${f.name} (unsupported type)`); continue; }
      if (f.size > MAX_BYTES) { rejected.push(`${f.name} (over 25 MB)`); continue; }
      incoming.push({ id: crypto.randomUUID(), file: f });
    }
    setItems((prev) => {
      const merged = [...prev, ...incoming];
      if (merged.length > MAX_FILES) {
        setError(`You can merge up to ${MAX_FILES} files at once.`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
    if (rejected.length) setError(`Skipped: ${rejected.join(', ')}.`);
  }

  function remove(id: string): void {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }

  function move(index: number, dir: -1 | 1): void {
    setItems((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  function onDrop(index: number): void {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === index) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved!);
      return next;
    });
  }

  async function run(compress: boolean): Promise<void> {
    if (items.length < 2) { setError('Add at least two files to merge.'); return; }
    setPending(compress ? 'compress' : 'merge'); setError(null);
    try {
      const name = filename.trim() || 'merged';
      await mergeApi.merge(items.map((i) => i.file), name, compress);
    } catch (err) {
      const f = err as MergeFailure;
      const base = ERROR_TEXT[f.code] ?? ERROR_TEXT.merge_failed!;
      setError(f.file ? `${base}: ${f.file}` : base);
    } finally {
      setPending(false);
    }
  }

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    void run(false);
  }

  return (
    <div className="page space-y-8">
      <header className="space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Tools</p>
        <h1 className="font-sans text-[2.25rem] md:text-[2.75rem] leading-none text-ink">
          Merge to PDF
        </h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Combine PDFs and images (JPG, PNG, HEIC, WebP, and more) into a single PDF.
          Drag to reorder, then download.
        </p>
      </header>

      {/* Hidden file input — mounted once so inputRef is always available. */}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,image/*"
        className="sr-only"
        onChange={(e) => { addFiles(e.target.files); if (inputRef.current) inputRef.current.value = ''; }}
      />

      <form onSubmit={submit} className="space-y-8">
        {items.length === 0 ? (
          /* EMPTY STATE — full-width drop zone */
          <div
            role="button"
            tabIndex={0}
            aria-label="Choose files to merge"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); }
            }}
            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            className={[
              'relative flex flex-col items-center justify-center gap-4 rounded-3xl border-2 border-dashed px-6 py-16 text-center cursor-pointer transition-colors',
              dragOver ? 'border-primary bg-primary/5' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-container))] hover:bg-[hsl(var(--surface-container-high))]',
            ].join(' ')}
          >
            <div className="grid size-16 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]">
              <Icon name="cloud_upload" size={30} />
            </div>
            <p className="text-[1.05rem] font-medium text-ink">Drag &amp; drop files here</p>
            <span className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] px-5 py-2.5 text-sm font-medium">
              <Icon name="folder_open" size={18} /> Browse files
            </span>
            <p className="text-xs text-muted-foreground">
              PDF &amp; images · up to {MAX_FILES} files · 25 MB each
            </p>
          </div>
        ) : (
          /* HAS FILES — two columns: workbench (left) + live preview (right) */
          <div className="grid gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
          {/* LEFT — ordered file list + filename + actions */}
          <div className="min-w-0 space-y-6">
          <ol className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-container-low))] overflow-hidden divide-y divide-[hsl(var(--border))]">
            {items.map((it, i) => {
              const pdf = isPdf(it.file);
              return (
                <li
                  key={it.id}
                  draggable
                  onDragStart={() => { dragIndex.current = i; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  className="group flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-[hsl(var(--surface-container-low))]"
                >
                  <span className="shrink-0 text-muted-foreground/60 cursor-grab active:cursor-grabbing" aria-hidden>
                    <Icon name="drag_indicator" size={18} />
                  </span>
                  <span className="w-5 shrink-0 text-center text-xs font-semibold text-muted-foreground tabular">{i + 1}</span>
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))]">
                    <Icon name={pdf ? 'description' : 'image'} size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink truncate" title={it.file.name}>{it.file.name}</p>
                    <p className="text-xs text-muted-foreground tabular">{pdf ? 'PDF' : 'Image'} · {fmtSize(it.file.size)}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="grid size-8 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    >
                      <Icon name="arrow_upward" size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === items.length - 1}
                      aria-label="Move down"
                      className="grid size-8 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    >
                      <Icon name="arrow_downward" size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(it.id)}
                      aria-label={`Remove ${it.file.name}`}
                      className="grid size-8 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
                    >
                      <Icon name="close" size={16} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Add files */}
          {items.length < MAX_FILES && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              aria-label="Add files"
              className={[
                'flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed py-3 text-sm font-medium transition-colors',
                dragOver ? 'border-primary bg-primary/5 text-primary' : 'border-[hsl(var(--border))] text-muted-foreground hover:bg-[hsl(var(--surface-container))] hover:text-foreground',
              ].join(' ')}
            >
              <Icon name="add" size={18} /> Add files
            </button>
          )}

          {/* Output filename */}
          <div className="space-y-1.5">
            <label htmlFor="merge-name" className="text-sm font-medium text-foreground">
              File name <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                id="merge-name"
                type="text"
                placeholder="merged"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                className="input flex-1"
              />
              <span className="text-sm text-muted-foreground">.pdf</span>
            </div>
          </div>

          {error && (
            <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Action bar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {`${items.length} file${items.length === 1 ? '' : 's'} selected`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void run(true)}
                disabled={items.length < 2 || pending !== false}
                title="Merge and shrink the result by downsampling images (~150 DPI)"
                className="btn-secondary whitespace-nowrap"
              >
                {pending === 'compress' ? 'Compressing…' : (<><Icon name="compress" size={16} /> Merge &amp; compress</>)}
              </button>
              <button type="submit" disabled={items.length < 2 || pending !== false} className="btn-primary whitespace-nowrap">
                {pending === 'merge' ? 'Merging…' : (<><Icon name="download" size={16} /> Merge &amp; download</>)}
              </button>
            </div>
          </div>
          </div>
          {/* end LEFT */}

          {/* RIGHT — live preview */}
          <div className="min-w-0 lg:sticky lg:top-6">
            {items.length >= 2 ? (
              <MergePreview items={items} />
            ) : (
              <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-3xl border border-[hsl(var(--border)/0.5)] bg-[hsl(var(--surface-container-low))] p-10 text-center">
                <span className="grid size-14 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]">
                  <Icon name="visibility" size={26} />
                </span>
                <p className="text-sm font-medium text-ink">Live preview</p>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Add at least two files to see how the merged PDF will look — it updates as you reorder.
                </p>
              </div>
            )}
          </div>
        </div>
        )}
      </form>
    </div>
  );
}
